// src/pages/Settings.tsx
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "../index.css";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { Timer } from "lucide-react";
import { ChevronDown } from "lucide-react";
import { Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { auth } from "@/lib/auth";
import { supabase } from "../supabaseClient";
import {
  getFontSizePreference,
  setFontSizePreference,
  type FontSizePreference,
} from "@/lib/fontSize";

import {
  User,
  LogOut,
  FileText,
  Shield,
  LifeBuoy,
  Info,
  ChevronsUpDown,
  Heart,
} from "lucide-react";

import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";

import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";

import type { User as SupabaseUser } from "@supabase/supabase-js";

// ========= 文案（改为从 i18n 读取） =========
const HELP_MD_KEY = "settings.helpMd";
const ABOUT_MD_KEY = "settings.aboutMd";

// ========= 下拉选项 =========
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 100 }, (_, i) => CURRENT_YEAR - i);

const COUNTRIES = [
  "Malaysia",
  "Singapore",
  "Thailand",
  "Indonesia",
  "Vietnam",
  "Philippines",
  "China",
  "Hong Kong",
  "Taiwan",
  "Japan",
  "South Korea",
  "India",
  "United States",
  "United Kingdom",
  "Australia",
  "Canada",
  "Germany",
  "France",
  "Italy",
  "Spain",
  "Netherlands",
  "Sweden",
  "Norway",
  "Brazil",
  "Mexico",
  "Other",
];

const OCCUPATION_CATEGORIES = [
  "Accounting / Finance",
  "Administration / Operations",
  "Design / Creative",
  "Engineering / Technical",
  "Education / Training",
  "Healthcare / Medical",
  "HR / People",
  "Legal / Compliance",
  "Marketing / Growth",
  "Product / Management",
  "Sales / Business Development",
  "Student",
  "Other",
];

// 只保留 3 个性别选项
const GENDER_OPTIONS = ["Prefer not to say", "Female", "Male"];

// ========= 主组件 =========
function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function decodeAndValidateVapidPublicKey(vapidPublicKey: string) {
  // Web Push requires an uncompressed P-256 public key (65 bytes, first byte 0x04).
  const bytes = urlBase64ToUint8Array(vapidPublicKey);
  if (bytes.length !== 65 || bytes[0] !== 4) {
    throw new Error(
      "Invalid VAPID public key. Expected a base64url-encoded uncompressed P-256 public key (65 bytes).",
    );
  }
  return bytes;
}

export default function Settings() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();

  const [fontSizePreference, setFontSizePreferenceState] = useState<FontSizePreference>(() =>
    getFontSizePreference(),
  );

  const PROFILE_UPDATED_EVENT = "innergem:profile-updated";

  const refreshEntitlements = async (expected?: { tier?: string; trialPrefix?: string }) => {
    // For newly registered users, the profile row might be created by redeem RPC.
    // Use a short retry loop to avoid requiring an app restart.
    for (let attempt = 0; attempt < 5; attempt++) {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const userId = user?.id;
      if (!userId) return;

      const { data } = await supabase
        .from("user_profiles")
        .select("has_lifetime_deal, subscription_tier, role, trial_code, trial_expires_at")
        .eq("id", userId)
        .maybeSingle();

      if (data) {
        const rawTier = (data as any)?.subscription_tier as string | null;
        const tier = rawTier ? rawTier.toLowerCase().replace(/\s+/g, "_") : null;
        setSubscriptionTier(tier ?? null);
        setRole((data as any).role ?? null);

        const nextTrialCode = ((data as any)?.trial_code as string | null) ?? null;
        const nextTrialExpiresAt = ((data as any)?.trial_expires_at as string | null) ?? null;
        setTrialCode(nextTrialCode);
        setTrialExpiresAt(nextTrialExpiresAt);

        const hasLifetime = !!(data as any).has_lifetime_deal || tier === "lifetime_deal";
        setHasLifetimeDeal(!!hasLifetime);

        const expectedTierOk = expected?.tier ? tier === expected.tier : true;
        const expectedTrialOk = expected?.trialPrefix
          ? !!nextTrialCode && nextTrialCode.toUpperCase().startsWith(expected.trialPrefix.toUpperCase())
          : true;

        if (expectedTierOk && expectedTrialOk) return;
      }

      await new Promise((r) => setTimeout(r, 250));
    }
  };

  // ✅ 进入 Settings 时隐藏整页滚动条，离开时恢复
  useEffect(() => {
    const htmlEl = document.documentElement;
    const bodyEl = document.body;

    htmlEl.classList.add("body-hide-scrollbar");
    bodyEl.classList.add("body-hide-scrollbar");

    return () => {
      htmlEl.classList.remove("body-hide-scrollbar");
      bodyEl.classList.remove("body-hide-scrollbar");
    };
  }, []);

  // auth 用户（主要用来拿 email）
  const authUser = auth.getCurrentUser();

  // Supabase user（给 profile / user_profiles 用）
  const [supabaseUser, setSupabaseUser] = useState<SupabaseUser | null>(null);

  const [email, setEmail] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [country, setCountry] = useState("");
  const [gender, setGender] = useState("");
  const [occupation, setOccupation] = useState("");
  const [role, setRole] = useState<string | null>(null);
  const [mentorDescription, setMentorDescription] = useState("");

  // Push notifications (Web Push) - available to all users
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  const pushSupported =
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window;

  const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [isProfileSaving, setIsProfileSaving] = useState(false);

  // Lifetime Deal Logic
  const [redemptionCode, setRedemptionCode] = useState("");
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [hasLifetimeDeal, setHasLifetimeDeal] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState<string | null>(null);

  // GEMTRIAL (LTD-like) trial tracking
  const [trialCode, setTrialCode] = useState<string | null>(null);
  const [trialExpiresAt, setTrialExpiresAt] = useState<string | null>(null);

  // 从 Supabase 读当前 user
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setSupabaseUser(data.user ?? null);
      setEmail(data.user?.email ?? null);
    });
  }, []);

  // 读取 profile
  useEffect(() => {
    const fetchProfile = async (userId: string) => {
      const { data, error } = await supabase
        .from("user_profiles")
        .select(
          "full_name, birth_year, country, gender, occupation, has_lifetime_deal, subscription_tier, role, mentor_description, trial_code, trial_expires_at",
        )
        .eq("id", userId)
        .maybeSingle();

      if (error || !data) return;

      setDisplayName(data.full_name ?? "");
      setBirthYear(data.birth_year ? String(data.birth_year) : "");
      setCountry(data.country ?? "");
      setGender(data.gender ?? "");
      setOccupation(data.occupation ?? "");
      setRole((data as any).role ?? null);
      setMentorDescription((data as any).mentor_description ?? "");

      const rawTier = (data as any)?.subscription_tier as string | null;
      const tier = rawTier ? rawTier.toLowerCase().replace(/\s+/g, "_") : null;
      setSubscriptionTier(tier ?? null);

      const rawTrialCode = ((data as any)?.trial_code as string | null) ?? null;
      setTrialCode(rawTrialCode);
      const rawTrialExpiresAt = ((data as any)?.trial_expires_at as string | null) ?? null;
      setTrialExpiresAt(rawTrialExpiresAt);

      const hasLifetime = !!data.has_lifetime_deal || tier === "lifetime_deal";
      setHasLifetimeDeal(!!hasLifetime);
    };

    const loadProfile = async () => {
      if (!supabaseUser) {
        setIsProfileLoading(false);
        return;
      }

      await fetchProfile(supabaseUser.id);
      setIsProfileLoading(false);
    };

    loadProfile();
  }, [supabaseUser]);

  // Detect existing push subscription
  useEffect(() => {
    if (!pushSupported) return;

    (async () => {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setPushEnabled(!!subscription);
      } catch (err) {
        console.warn("Failed to detect push subscription", err);
      }
    })();
  }, [pushSupported]);

  const setPushNotifications = async (enabled: boolean) => {
    if (!pushSupported) {
      toast({
        title: t("settings.push.notSupportedTitle", {
          defaultValue: "Notifications not supported",
        }),
        description: t("settings.push.notSupportedDesc", {
          defaultValue: "Your browser/device does not support push notifications.",
        }),
        variant: "destructive",
      });
      return;
    }

    setPushLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;

      if (enabled) {
        if (!vapidPublicKey) {
          toast({
            title: t("settings.push.missingVapidTitle", {
              defaultValue: "Push not configured",
            }),
            description: t("settings.push.missingVapidDesc", {
              defaultValue:
                "Missing VITE_VAPID_PUBLIC_KEY. Create .env.local (see .env.local.example), set VITE_VAPID_PUBLIC_KEY, then restart npm run dev.",
            }),
            variant: "destructive",
          });
          return;
        }

        let applicationServerKey: Uint8Array;
        try {
          applicationServerKey = decodeAndValidateVapidPublicKey(vapidPublicKey);
        } catch (e: any) {
          toast({
            title: t("settings.push.missingVapidTitle", {
              defaultValue: "Push not configured",
            }),
            description: t("settings.push.missingVapidDesc", {
              defaultValue:
                "VITE_VAPID_PUBLIC_KEY is present but not a valid Web Push VAPID public key. Generate a new pair with `npm run vapid:generate -- --write`, then copy the Public Key (base64url, usually ~87 chars) into .env.local and restart `npm run dev`.",
            }),
            variant: "destructive",
          });
          return;
        }

        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
          setPushEnabled(false);
          return;
        }

        const existing = await registration.pushManager.getSubscription();
        const subscription = existing ?? (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          // TS lib.dom typing can be overly strict about ArrayBuffer vs SharedArrayBuffer.
          // Runtime expects a BufferSource; applicationServerKey is validated above.
          applicationServerKey: applicationServerKey as unknown as BufferSource,
        }));

        const { error } = await supabase.functions.invoke("register-push-subscription", {
          body: { subscription: subscription.toJSON() },
        });

        if (error) throw error;

        setPushEnabled(true);
        return;
      }

      const current = await registration.pushManager.getSubscription();
      const endpoint = current?.endpoint;

      if (current) {
        await current.unsubscribe();
      }

      const { error } = await supabase.functions.invoke("unregister-push-subscription", {
        body: { endpoint },
      });

      if (error) throw error;

      setPushEnabled(false);
    } catch (err: any) {
      console.error("Failed to toggle push notifications", err);

      const httpStatus = err?.context?.response?.status;
      const httpStatusText = err?.context?.response?.statusText;
      const message =
        err?.message ||
        err?.error_description ||
        err?.details ||
        (typeof err === "string" ? err : "Unknown error");

      const suffix = httpStatus
        ? ` (HTTP ${httpStatus}${httpStatusText ? ` ${httpStatusText}` : ""})`
        : "";

      const baseDesc = t("settings.push.failedDesc", {
        defaultValue: "Could not update notifications. Please try again.",
      });

      const detail = `${message}${suffix}`;

      toast({
        title: t("settings.push.failedTitle", { defaultValue: "Update failed" }),
        description: `${baseDesc} (${detail})`,
        variant: "destructive",
      });
    } finally {
      setPushLoading(false);
    }
  };

  const membershipLabel = useMemo(() => {
    // 1) role-based override: admin / mentor 不走付费逻辑
    if (role === "admin") {
      return t("settings.membership_admin", { defaultValue: "Admin" });
    }

    if (role === "mentor") {
      return t("settings.membership_mentor", { defaultValue: "Mentor" });
    }

    // 2) 普通 user：终身会员（或 LTD-like 试用期）
    const tier = subscriptionTier?.toLowerCase() || null;
    if (hasLifetimeDeal || tier === "lifetime_deal") {
      return t("settings.lifetime");
    }

    if (!tier) {
      return t("settings.free");
    }

    switch (tier) {
      case "beginner_monthly":
        return t("settings.membership_beginner_monthly", { defaultValue: "Beginner Monthly" });
      case "beginner_yearly":
        return t("settings.membership_beginner_yearly", { defaultValue: "Beginner Yearly" });
      case "intermediate_monthly":
        return t("settings.membership_intermediate_monthly", { defaultValue: "Intermediate Monthly" });
      case "intermediate_yearly":
        return t("settings.membership_intermediate_yearly", { defaultValue: "Intermediate Yearly" });
      case "upper_intermediate_monthly":
        return t("settings.membership_upper_intermediate_monthly", { defaultValue: "Upper Intermediate Monthly" });
      case "upper_intermediate_yearly":
        return t("settings.membership_upper_intermediate_yearly", { defaultValue: "Upper Intermediate Yearly" });
      case "beginner":
        return t("settings.membership_beginner", { defaultValue: "Beginner" });
      case "intermediate":
        return t("settings.membership_intermediate", { defaultValue: "Intermediate" });
      case "upper_intermediate":
        return t("settings.membership_upper_intermediate", { defaultValue: "Upper Intermediate" });
      default:
        return t("settings.free");
    }
  }, [hasLifetimeDeal, subscriptionTier, role, t]);

  const isGemTrialActive = useMemo(() => {
    const code = (trialCode || "").toUpperCase();
    if (code === "NAS@INNERGEM30") {
      if (!trialExpiresAt) return true;
      const expiresMs = new Date(trialExpiresAt).getTime();
      if (Number.isNaN(expiresMs)) return true;
      return expiresMs > Date.now();
    }

    if (!code.startsWith("GEMTRIAL-")) return false;

    if (!trialExpiresAt) return true; // treat missing expiry as active (legacy/backfill scenarios)
    const expiresMs = new Date(trialExpiresAt).getTime();
    if (Number.isNaN(expiresMs)) return true;
    return expiresMs > Date.now();
  }, [trialCode, trialExpiresAt]);

  const trialBadgeTitle = useMemo(() => {
    if (!isGemTrialActive) return "";
    if (!trialExpiresAt) {
      return t("settings.trialBadgeTooltipNoDate", {
        defaultValue: "Limited-time access (expiry pending)",
      });
    }

    const d = new Date(trialExpiresAt);
    if (Number.isNaN(d.getTime())) {
      return t("settings.trialBadgeTooltipNoDate", {
        defaultValue: "Limited-time access (expiry pending)",
      });
    }

    return t("settings.trialBadgeTooltip", {
      defaultValue: "Limited-time access · Expires {{date}}",
      date: d.toLocaleString(),
    });
  }, [isGemTrialActive, trialExpiresAt, t]);

  const isPaidMembership = useMemo(() => {
    if (hasLifetimeDeal) return true;

    const tier = (subscriptionTier || "").toLowerCase();
    if (!tier || tier === "free") return false;

    const paidTiers = [
      "beginner",
      "intermediate",
      "upper_intermediate",
      "lifetime_deal",
      "beginner_monthly",
      "beginner_yearly",
      "intermediate_monthly",
      "intermediate_yearly",
      "upper_intermediate_monthly",
      "upper_intermediate_yearly",
    ];

    return paidTiers.includes(tier);
  }, [hasLifetimeDeal, subscriptionTier]);

  // 保存 profile
  const handleSaveProfile = async () => {
    if (!supabaseUser) {
      console.warn("handleSaveProfile: supabaseUser is null");
      toast({
        title: "Not signed in",
        description: "Please log in again and then save your profile.",
        variant: "destructive",
      });
      return;
    }

    setIsProfileSaving(true);

    const updates = {
      id: supabaseUser.id,
      full_name: displayName || null,
      birth_year: birthYear ? Number(birthYear) : null,
      country: country || null,
      gender: gender || null,
      occupation: occupation || null,
      mentor_description: mentorDescription || null,
      updated_at: new Date().toISOString(),
    };

    const { error } = await supabase
      .from("user_profiles")
      .upsert(updates, { onConflict: "id" });

    setIsProfileSaving(false);

    if (error) {
      console.error(error);
      toast({
        title: "Save failed",
        description: "Couldn't update your profile. Please try again.",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Profile updated",
        description: "Your Inner Gem profile has been saved.",
      });
    }
  };

  // 登出
  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error("Sign out failed:", error);
      toast({
        title: "Sign out failed",
        description: "Please try again in a moment.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Signed out",
      description: "See you next time, Inner Gem.",
    });

    navigate("/login", { replace: true });
  };

  const handleRedeem = async () => {
    if (!redemptionCode.trim()) return;
    setIsRedeeming(true);

    try {
      const code = redemptionCode.trim();
      const normalized = code.toUpperCase();

      // NAS@INNERGEM30 grants a 30-day "all-app except Mentor Deep Dive" trial
      if (normalized === "NAS@INNERGEM30") {
        const { data, error } = await supabase.rpc("redeem_nas_trial_code", {
          code_input: code,
        });

        if (error) throw error;

        if (data && data.success) {
          toast({
            title: "Success!",
            description: data.message,
          });

          setSubscriptionTier("lifetime_deal");
          setRedemptionCode("");

          await refreshEntitlements({ tier: "lifetime_deal", trialPrefix: "NAS@INNERGEM30" });

          window.dispatchEvent(new Event(PROFILE_UPDATED_EVENT));
          return;
        }

        toast({
          title: "Redemption failed",
          description: data?.message || "Invalid code",
          variant: "destructive",
        });
        return;
      }

      // GEMMDD codes grant 1-month Mentor Deep Dive unlimited credits
      if (normalized.startsWith("GEMMDD-")) {
        const { data, error } = await supabase.rpc("redeem_mentor_deepdive_code", {
          code_input: code,
        });

        if (error) throw error;

        if (data && data.success) {
          toast({
            title: "Success!",
            description: data.message,
          });

          setRedemptionCode("");
          window.dispatchEvent(new Event(PROFILE_UPDATED_EVENT));
          return;
        }

        toast({
          title: "Redemption failed",
          description: data?.message || "Invalid code",
          variant: "destructive",
        });
        return;
      }

      // GEMTRIAL codes activate a 3-month LTD-like trial (no Mentor Deep Dive credit included)
      if (normalized.startsWith("GEMTRIAL-")) {
        const { data, error } = await supabase.rpc("redeem_trial_code", {
          code_input: code,
        });

        if (error) throw error;

        if (data && data.success) {
          toast({
            title: "Success!",
            description: data.message,
          });

          // Trial should behave like LTD for app access/limits
          setSubscriptionTier("lifetime_deal");
          setRedemptionCode("");

          // Force-refresh entitlements immediately (works even if supabaseUser state isn't ready yet)
          await refreshEntitlements({ tier: "lifetime_deal", trialPrefix: "GEMTRIAL" });

          window.dispatchEvent(new Event(PROFILE_UPDATED_EVENT));
          return;
        }

        toast({
          title: "Redemption failed",
          description: data?.message || "Invalid code",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase.rpc("redeem_lifetime_code", {
        code_input: code,
      });

      if (error) throw error;

      if (data && data.success) {
        toast({
          title: "Success!",
          description: data.message,
        });
        setSubscriptionTier("lifetime_deal");
        setRedemptionCode("");

        // Force-refresh entitlements immediately (works even if profile row was created by RPC)
        await refreshEntitlements({ tier: "lifetime_deal" });

        window.dispatchEvent(new Event(PROFILE_UPDATED_EVENT));
      } else {
        toast({
          title: "Redemption failed",
          description: data?.message || "Invalid code",
          variant: "destructive",
        });
      }
    } catch (err: any) {
      console.error("Redemption error:", err);
      toast({
        title: "Error",
        description: err.message || "Something went wrong",
        variant: "destructive",
      });
    } finally {
      setIsRedeeming(false);
    }
  };

  const effectiveEmail = authUser?.email ?? email ?? "";

  // 切换语言
  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("i18nextLng", lng);
  };

  const changeFontSizePreference = (next: FontSizePreference) => {
    setFontSizePreferenceState(next);
    setFontSizePreference(next);
  };

  return (
    <div className="container max-w-4xl mx-auto py-8 px-4">
      <div className="mx-auto flex max-w-4xl flex-col gap-0">
        {/* 标题区：居中 */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-primary mb-2">{t("settings.title")}</h1>
          <p className="text-muted-foreground">{t("settings.subtitle")}</p>
        </div>

        {/* 语言切换卡片 */}
        <Card className="shadow-[var(--shadow-soft)] mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" />
              {t("settings.language")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Button
                variant={i18n.language?.startsWith("en") ? "default" : "outline"}
                onClick={() => changeLanguage("en")}
                size="sm"
              >
                English
              </Button>
              <Button
                variant={i18n.language?.startsWith("zh") ? "default" : "outline"}
                onClick={() => changeLanguage("zh")}
                size="sm"
              >
                中文 (简体)
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Font size (accessibility) */}
        <Card className="shadow-[var(--shadow-soft)] mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              {t("settings.fontSize.title", { defaultValue: "Font size" })}
            </CardTitle>
            <CardDescription>
              {t("settings.fontSize.desc", {
                defaultValue: "Choose a larger text size if you find the app hard to read.",
              })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Button
                variant={fontSizePreference === "normal" ? "default" : "outline"}
                onClick={() => changeFontSizePreference("normal")}
                size="sm"
              >
                {t("settings.fontSize.normal", { defaultValue: "Normal" })}
              </Button>
              <Button
                variant={fontSizePreference === "large" ? "default" : "outline"}
                onClick={() => changeFontSizePreference("large")}
                size="sm"
              >
                {t("settings.fontSize.large", { defaultValue: "Large" })}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Account 卡片 */}
        <Card className="shadow-[var(--shadow-soft)] mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              {t('settings.account')}
            </CardTitle>
            <CardDescription>{t('settings.account_desc', { defaultValue: 'Your Inner Gem account details' })}</CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Email */}
            <div>
              <p className="text-sm text-muted-foreground">{t('settings.email')}</p>
              <p className="font-medium">{effectiveEmail}</p>
            </div>

            {/* Lifetime Status + Unlock/Support */}
            <div>
              <p className="text-sm text-muted-foreground">{t('settings.membership')}</p>
              <div className="flex items-center gap-2 mt-1">
                {isPaidMembership ? (
                  <div className="inline-flex items-center gap-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      {membershipLabel}
                    </span>
                    {isGemTrialActive && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100"
                        title={trialBadgeTitle}
                      >
                        <Timer className="h-3 w-3" />
                        {t("settings.trialBadge", { defaultValue: "体验中" })}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-sm">{membershipLabel}</span>
                )}

                {/* Small Unlock / Support button on the same row (aligned right, gold gradient) */}
                <Dialog>
                  <DialogTrigger asChild>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="ml-auto h-7 rounded-full border border-white/20 bg-gradient-to-r from-[#C9A961] via-[#F4E4B7] to-[#C9A961] px-3 text-xs font-semibold text-slate-900 shadow-[0_6px_12px_rgba(232,191,91,0.45)] hover:brightness-105 transition-all duration-300"
                    >
                      <Heart className="h-3 w-3 mr-1 text-slate-900 fill-slate-900/10" />
                      <span>{t("support.unlock", { defaultValue: "Unlock / Support" })}</span>
                    </Button>
                  </DialogTrigger>

                  {/* Same support dialog content as in the sidebar */}
                  <DialogContent
                    className="
                      max-w-lg w-[92vw] sm:w-full 
                      max-h-[85vh] 
                      flex flex-col 
                      p-0
                      gap-0
                      overflow-hidden
                      rounded-2xl
                    "
                  >
                    {/* Header */}
                    <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
                      <DialogTitle className="text-2xl text-center font-bold text-purple-900 flex flex-col items-center gap-3">
                        <div className="p-3 bg-purple-50 rounded-full">
                          <Heart className="w-8 h-8 text-red-500 fill-red-500" />
                        </div>
                        {t("support.title", { defaultValue: "Support Inner Gem" })}
                      </DialogTitle>
                      <DialogDescription className="sr-only">
                        {t("support.dialogDesc", { defaultValue: "Information about how to support Inner Gem and Tres Ong." })}
                      </DialogDescription>
                    </DialogHeader>

                    {/* Body: scrollable area */}
                    <div
                      className="
                        flex-1
                        px-6
                        pb-4
                        space-y-4
                        text-[15px] sm:text-sm
                        text-gray-600
                        leading-relaxed
                        overflow-y-auto
                      "
                    >
                      <p>{t("support.modal.intro1", { defaultValue: "I'm Tres, the solo creator behind Inner Gem. My deepest wish is that more and more of us can gently transition from the dense, fast-paced 3D world into a 5D New Earth — a way of living with more awareness, compassion, and inner freedom." })}</p>
                      <p>{t("support.modal.intro2", { defaultValue: "Inner Gem is not the destination; it's a small doorway, a humble companion that walks with you as you remember who you really are." })}</p>
                      <div className="bg-purple-50 p-4 rounded-xl border border-purple-100 text-purple-900 font-medium italic">
                        <p>{t("support.modal.quote", { defaultValue: "When you remember you are the director, you no longer fight these actors. You acknowledge them, guide them, and let them step back when their scene is complete." })}</p>
                      </div>
                      <p>{t("support.modal.body1", { defaultValue: "Your support helps me continue building features, guides, and spaces that remind you of this truth: you are not your passing emotions; you are the consciousness that can see them, hold them, and lovingly direct the story of your life." })}</p>

                      <div className="my-6 p-4 bg-purple-50 rounded-lg border border-purple-100 text-sm">
                        <h4 className="font-semibold text-purple-900 mb-2">
                          {t("support.modal.membershipTitle", { defaultValue: "Membership Options" })}
                        </h4>
                        <p className="text-purple-800 mb-3">
                          {t("support.modal.membershipIntro", {
                            defaultValue:
                              "Quick comparison: the key differences are daily usage limits, mentor deep dive access, and pay-per-question rules.",
                          })}
                        </p>
                        <ul className="list-disc list-inside text-purple-800 space-y-1 ml-1">
                          <li>
                            <span className="font-semibold">
                              {t("settings.membership_beginner", { defaultValue: "Beginner" })}
                            </span>
                            <span className="ml-1">
                              {t("support.modal.membershipBeginnerPrice", {
                                defaultValue: "— SGD 250/year or SGD 25.99/month",
                              })}
                            </span>
                            <div className="ml-5 mt-1 text-xs text-purple-700/90 leading-snug">
                              {t("support.modal.membershipBeginnerHighlights", {
                                defaultValue:
                                  "3 SPARK + 3 GUIDED per day; can browse mentors but cannot start 1:1 deep dive chat; deeper meditation content stays locked.",
                              })}
                            </div>
                          </li>
                          <li>
                            <span className="font-semibold">
                              {t("settings.membership_intermediate", { defaultValue: "Intermediate" })}
                            </span>
                            <span className="ml-1">
                              {t("support.modal.membershipIntermediatePrice", {
                                defaultValue: "— SGD 300/year or SGD 29.99/month",
                              })}
                            </span>
                            <div className="ml-5 mt-1 text-xs text-purple-700/90 leading-snug">
                              {t("support.modal.membershipIntermediateHighlights", {
                                defaultValue:
                                  "5 SPARK + 5 GUIDED per day; can start 1:1 mentor deep dive (pay-per-question: 1 question = 1 credit).",
                              })}
                            </div>
                          </li>
                          <li>
                            <span className="font-semibold">
                              {t("settings.membership_upper_intermediate", { defaultValue: "Upper Intermediate" })}
                            </span>
                            <span className="ml-1">
                              {t("support.modal.membershipUpperIntermediatePrice", {
                                defaultValue: "— SGD 400/year or SGD 39.99/month",
                              })}
                            </span>
                            <div className="ml-5 mt-1 text-xs text-purple-700/90 leading-snug">
                              {t("support.modal.membershipUpperIntermediateHighlights", {
                                defaultValue:
                                  "10 SPARK + 10 GUIDED per day; can start 1:1 mentor deep dive (pay-per-question) and unlock member-only discount.",
                              })}
                            </div>
                          </li>
                        </ul>
                        <p className="mt-3 text-xs text-purple-700/90 leading-snug">
                          {t("support.modal.membershipMentorCreditNote", {
                            defaultValue:
                              "Mentor Deep Dive is an add-on service: without a GEMMDD code, each question uses 1 paid credit; with an active GEMMDD pass, you can ask without per-question payment during the pass period.",
                          })}
                        </p>
                      </div>

                      <div className="my-4">
                        <stripe-pricing-table
                          pricing-table-id="prctbl_1TuCIvF701n8ruTe8SaUAHjC"
                          publishable-key="pk_live_51SR3mtF701n8ruTeUIDGJA4FV5ajQtTm0cb2Ce60Xd1sjAUESmGcQFwbNLUR9cAaT8a8jJXAxMXYeW2uzQUld64z00HryIrw8P"
                        ></stripe-pricing-table>
                      </div>

                      <div className="text-right pt-2">
                        <p className="font-serif italic text-gray-500">{t("support.modal.signatureThanks", { defaultValue: "With gratitude," })}</p>
                        <p className="font-bold text-gray-800">{t("support.modal.signatureName", { defaultValue: "Tres Ong" })}</p>
                        <p className="text-xs text-gray-400">{t("support.modal.signatureRole", { defaultValue: "Founder, Inner Gem" })}</p>
                      </div>
                    </div>

                    {/* Stripe pricing table handles its own checkout buttons now; no extra footer CTA needed. */}
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Redemption Input (Always visible) */}
            <div className="bg-muted/30 p-4 rounded-lg space-y-3 border">
              <div className="space-y-1">
                <p className="text-sm font-medium">{t('settings.redeem.haveCode', { defaultValue: 'Have a redemption code?' })}</p>
                <p className="text-xs text-muted-foreground">
                  {t('settings.redeem.hint', { defaultValue: 'Enter your code to unlock benefits.' })}
                </p>
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder={t('settings.redeem.placeholder', { defaultValue: 'Enter code (e.g. GEMTRIAL-XXXX / GEMMDD-XXXX)' })}
                  value={redemptionCode}
                  onChange={(e) => setRedemptionCode(e.target.value)}
                  className="bg-background"
                />
                <Button
                  onClick={handleRedeem}
                  disabled={isRedeeming || !redemptionCode.trim()}
                >
                  {isRedeeming ? t('settings.redeem.redeeming', { defaultValue: 'Redeeming...' }) : t('settings.redeem.cta', { defaultValue: 'Redeem' })}
                </Button>
              </div>
            </div>

            <Separator />

            {/* Profile 表单 */}
            {isProfileLoading ? (
              <p className="text-sm text-muted-foreground">
                {t('settings.profile.loading', { defaultValue: 'Loading profile...' })}
              </p>
            ) : (
              <div className="space-y-6">
                <div className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    {/* Display name */}
                    <div className="space-y-1">
                      <p className="text-sm font-medium">{t('settings.profile.display_name')}</p>
                      <Input
                        placeholder={t('settings.profile.display_name_placeholder', { defaultValue: 'Your Inner Gem name' })}
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                      />
                    </div>

                    {/* Birth year */}
                    <AutocompleteSelect
                      label={t('settings.profile.birth_year')}
                      placeholder={t('settings.profile.birth_year_placeholder', { defaultValue: '1993' })}
                      value={birthYear}
                      onChange={(v) => setBirthYear(v)}
                      options={YEARS.map(String)}
                    />

                    {/* Country */}
                    <AutocompleteSelect
                      label={t('settings.profile.country')}
                      placeholder={t('settings.profile.country_placeholder', { defaultValue: 'Malaysia' })}
                      value={country}
                      onChange={setCountry}
                      options={COUNTRIES}
                    />

                    {/* Occupation */}
                    <AutocompleteSelect
                      label={t('settings.profile.occupation')}
                      placeholder={t('settings.profile.occupation_placeholder', { defaultValue: 'e.g. Interior design / creative' })}
                      value={occupation}
                      onChange={setOccupation}
                      options={OCCUPATION_CATEGORIES}
                    />
                  </div>

                  {/* Gender */}
                  <AutocompleteSelect
                    label={t('settings.profile.gender')}
                    placeholder={t('settings.profile.gender_placeholder', { defaultValue: 'Prefer not to say' })}
                    value={gender}
                    onChange={setGender}
                    options={GENDER_OPTIONS}
                  />
                </div>

                {/* Notifications (all users) */}
                <div className="mt-2 rounded-xl border bg-muted/30 p-4 space-y-3">
                  <p className="text-sm font-semibold">
                    {t('settings.push.sectionTitle', { defaultValue: 'Notifications' })}
                  </p>
                  <div className="rounded-lg border bg-background/60 px-3 py-2 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">
                        {t('settings.push.title', { defaultValue: 'Message notifications' })}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('settings.push.desc', {
                          defaultValue:
                            'Enable system notifications when you receive new messages (recommended on mobile after installing the app).',
                        })}
                      </p>
                    </div>
                    <Switch
                      checked={pushEnabled}
                      onCheckedChange={(v) => void setPushNotifications(!!v)}
                      disabled={pushLoading}
                      aria-label={t('settings.push.title', { defaultValue: 'Message notifications' })}
                    />
                  </div>

                  {!pushSupported && (
                    <p className="text-xs text-muted-foreground">
                      {t('settings.push.notSupportedInline', {
                        defaultValue:
                          'Push notifications are not supported on this browser/device. On iOS, this requires iOS 16.4+ and adding the app to Home Screen.',
                      })}
                    </p>
                  )}
                </div>

                {/* Mentor-only settings */}
                {role === 'mentor' && (
                  <div className="mt-2 rounded-xl border bg-muted/30 p-4 space-y-3">
                    <p className="text-sm font-semibold">
                      {t('settings.mentor.title', { defaultValue: 'Mentor profile (only visible in Mentor Deep Dive)' })}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('settings.mentor.desc', {
                        defaultValue:
                          'Share a short description of how you support Inner Gem users. This appears on your Mentor Deep Dive card.',
                      })}
                    </p>

                    <Textarea
                      value={mentorDescription}
                      onChange={(e) => setMentorDescription(e.target.value)}
                      className="min-h-[120px] bg-background resize-none overflow-y-auto no-scrollbar"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      {t('settings.mentor.helper', {
                        defaultValue: 'If you leave this empty, a default Inner Gem mentor description will be used.',
                      })}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* 按钮区：保存 + 登出 */}
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleSaveProfile}
                disabled={isProfileSaving || isProfileLoading}
              >
                {isProfileSaving ? t('settings.profile.saving', { defaultValue: 'Saving...' }) : t('settings.profile.save_btn')}
              </Button>

              <Button
                type="button"
                variant="destructive"
                className="ml-auto"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4 mr-2" />
                {t('settings.profile.logout')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Help & About 卡片 */}
        <Card className="shadow-[var(--shadow-soft)] mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LifeBuoy className="h-5 w-5" />
              {t('settings.help.title', { defaultValue: 'Help & About' })}
            </CardTitle>
            <CardDescription>
              {t('settings.help.desc', { defaultValue: 'Learn how Inner Gem works and where to get support.' })}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-2">
            {/* Help 行 */}
            <Dialog>
              <DialogTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left hover:bg-accent"
                >
                  <LifeBuoy className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">
                    {t('settings.help.help', { defaultValue: 'Help' })}
                  </span>
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{t('settings.help.help', { defaultValue: 'Help' })}</DialogTitle>
                </DialogHeader>
                <div className="mt-2 whitespace-pre-line text-sm text-muted-foreground">
                  {t(HELP_MD_KEY)}
                </div>
              </DialogContent>
            </Dialog>

            {/* About 行 */}
            <Dialog>
              <DialogTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left hover:bg-accent"
                >
                  <Info className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">
                    {t('settings.help.about', { defaultValue: 'About Inner Gem' })}
                  </span>
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{t('settings.help.about', { defaultValue: 'About Inner Gem' })}</DialogTitle>
                </DialogHeader>
                <div className="mt-2 whitespace-pre-line text-sm text-muted-foreground">
                  {t(ABOUT_MD_KEY)}
                </div>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>

        {/* Legal 卡片 */}
        <Card className="shadow-[var(--shadow-soft)]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {t('settings.legal.title', { defaultValue: 'Legal' })}
            </CardTitle>
            <CardDescription>
              {t('settings.legal.desc', { defaultValue: 'Review our terms and privacy policy' })}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button variant="ghost" className="w-full justify-start" asChild>
              <a
                href="https://sites.google.com/view/inner-gem/terms?authuser=0"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Shield className="h-4 w-4 mr-2" />
                {t('settings.legal.terms', { defaultValue: 'Terms of Service' })}
              </a>
            </Button>

            <Button variant="ghost" className="w-full justify-start" asChild>
              <a
                href="https://sites.google.com/view/inner-gem/privacy?authuser=0"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Shield className="h-4 w-4 mr-2" />
                {t('settings.legal.privacy', { defaultValue: 'Privacy Policy' })}
              </a>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ========= 下拉组件（复用） =========
type AutocompleteSelectProps = {
  label: string;
  placeholder: string;
  value: string | undefined;
  onChange: (value: string) => void;
  options: string[];
};

function AutocompleteSelect({
  label,
  placeholder,
  value,
  onChange,
  options,
}: AutocompleteSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (!open) setInputValue("");
  }, [open]);

  const filteredOptions =
    inputValue.trim() === ""
      ? options
      : options.filter((opt) =>
        opt.toLowerCase().startsWith(inputValue.toLowerCase())
      );

  const hasCustomOption =
    inputValue.trim() !== "" &&
    !options.some(
      (opt) => opt.toLowerCase() === inputValue.trim().toLowerCase()
    );

  return (
    <div className="space-y-1">
      <p className="text-sm font-medium">{label}</p>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            onClick={() => {
              if (!open) setInputValue("");
            }}
          >
            <span className={value ? "" : "text-muted-foreground"}>
              {value || placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
          </button>
        </PopoverTrigger>

        <PopoverContent className="w-[280px] p-0">
          <Command>
            <CommandInput
              placeholder={placeholder}
              value={inputValue}
              onValueChange={setInputValue}
            />
            <CommandList>
              <CommandEmpty>{t('settings.autocomplete.empty', { defaultValue: 'No option found.' })}</CommandEmpty>
              <CommandGroup>
                {hasCustomOption && (
                  <CommandItem
                    key={`custom-${inputValue}`}
                    value={inputValue}
                    onSelect={() => {
                      onChange(inputValue.trim());
                      setOpen(false);
                    }}
                  >
                    {t('settings.autocomplete.use', { defaultValue: 'Use "{{value}}"', value: inputValue.trim() })}
                  </CommandItem>
                )}

                {filteredOptions.map((opt) => (
                  <CommandItem
                    key={opt}
                    value={opt}
                    onSelect={() => {
                      onChange(opt);
                      setOpen(false);
                    }}
                  >
                    {opt}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
// ========= 结束 =========